import { NotificationBell } from "@expert/common-ui/assets";
import { getLogger } from "@expert/logging";
import { Badge, Box, Button, Group, Menu, Text } from "@mantine/core";
import { type JSX, useState } from "react";
import { useOutageNotifications } from "../../hooks";
import { formatServiceName } from "../../utils";

import classes from "./NotificationCenter.module.css";

const logger = getLogger({ module: "NotificationCenterForOutages", supportTeam: "solve" });

export function NotificationCenter(): JSX.Element {
    const [opened, setOpened] = useState(false);
    const { outageAlerts, unreadCount, markAsRead, markAllAsRead } = useOutageNotifications(logger);

    const handleOpenMenu = () => {
        setOpened(true);
    };

    const handleCloseMenu = () => {
        if (opened) {
            markAllAsRead();
        }
    };

    return (
        <Menu
            width={322}
            shadow="sm"
            zIndex="var(--mantine-priority-highest)"
            opened={opened}
            onChange={setOpened}
            radius="md"
            closeOnClickOutside
            position="bottom-start"
            onOpen={handleOpenMenu}
            onClose={handleCloseMenu}
            closeOnItemClick={false}
        >
            <Menu.Target>
                <Button
                    variant="subtle"
                    color="gray"
                    className={classes.notificationButton}
                    data-menu-open={opened}
                    data-testid="notification-center"
                    id="notification-center-button"
                    size="xs"
                    mr={0}
                    onClick={() => setOpened((o) => !o)}
                >
                    <NotificationBell color="var(--mantine-color-white)" />
                    {unreadCount > 0 && (
                        <Badge size="xs" color="red" variant="filled" className={classes.notificationBadge}>
                            {unreadCount}
                        </Badge>
                    )}
                </Button>
            </Menu.Target>
            <Menu.Dropdown pb="md" pl="0" pr="0" pt="0">
                <Box className={classes.notificationHeader}>
                    <Text fw={700} size="md">
                        Notifications
                    </Text>
                </Box>
                {outageAlerts.length > 0 ? (
                    outageAlerts.map((alert) => (
                        <Menu.Item
                            key={alert.id}
                            className={`${classes.outageAlert} ${!alert.read ? classes.unreadAlert : ""}`}
                            onClick={() => markAsRead(alert.id)}
                        >
                            <Group gap="3xs" mb="3xs">
                                <Text size="sm" fw={700}>
                                    {formatServiceName(alert.serviceName)} service outage
                                </Text>
                                <Box
                                    className={`${classes.statusTag} ${
                                        alert.status === "active" ? classes.activeTag : classes.clearTag
                                    }`}
                                >
                                    <Text size="xs" fw={500} c="white">
                                        {alert.status.charAt(0).toUpperCase() + alert.status.slice(1)}
                                    </Text>
                                </Box>
                            </Group>
                            <Text size="sm">
                                {alert.timeAgo} - {alert.reportCount} reports
                            </Text>
                        </Menu.Item>
                    ))
                ) : (
                    <Box py="md" mx="md" ta="left">
                        <Text size="sm">No notifications at this time</Text>
                    </Box>
                )}
            </Menu.Dropdown>
        </Menu>
    );
}
